import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  ArrowLeft,
  Sparkles,
  Plus,
  Trash2,
  Layers,
  Wand2,
  FolderTree,
  Settings as Cog,
  Mail,
  Server,
  FileCode,
  Download,
  ExternalLink,
} from "lucide-react";
import { API } from "../Sidebar";

export const AffiliateSiteDetailPage = () => {
  const { siteId } = useParams();
  const [site, setSite] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [stacks, setStacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, c, t, st] = await Promise.all([
        axios.get(`${API}/affiliate/sites/${siteId}`),
        axios.get(`${API}/affiliate/sites/${siteId}/categories`),
        axios.get(`${API}/affiliate/sites/${siteId}/tools`),
        axios.get(`${API}/affiliate/sites/${siteId}/stacks`),
      ]);
      setSite(s.data);
      setCategories(c.data);
      setTools(t.data);
      setStacks(st.data);
    } catch (e) {
      toast.error("Échec du chargement du site");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  if (loading) return <p className="text-slate-500">Chargement…</p>;
  if (!site) return <p className="text-slate-500">Site introuvable.</p>;

  return (
    <div data-testid="affiliate-site-detail-page">
      <Link
        to="/admin/affiliate/sites"
        className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </Link>

      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-orange-500" />
            <h2
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {site.name}
            </h2>
            <Badge variant="outline" className="uppercase">
              {site.lang}
            </Badge>
          </div>
          {site.tagline && (
            <p className="text-slate-600 max-w-2xl">{site.tagline}</p>
          )}
          {site.domain && (
            <a
              href={site.domain}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 hover:underline text-sm mt-1 inline-block"
            >
              {site.domain}
            </a>
          )}
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <StatBadge label="Catégories" value={categories.length} />
          <StatBadge label="Outils" value={tools.length} />
          <StatBadge label="Stacks" value={stacks.length} />
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList data-testid="aff-detail-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <FolderTree className="w-4 h-4 mr-1" /> Vue
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            Catégories
          </TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools">
            Outils ({tools.length})
          </TabsTrigger>
          <TabsTrigger value="stacks" data-testid="tab-stacks">
            <Layers className="w-4 h-4 mr-1" /> Stacks
          </TabsTrigger>
          <TabsTrigger value="leadmagnet" data-testid="tab-leadmagnet">
            <Mail className="w-4 h-4 mr-1" /> Lead Magnet
          </TabsTrigger>
          <TabsTrigger value="generate" data-testid="tab-generate">
            <FileCode className="w-4 h-4 mr-1" /> Génération
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Cog className="w-4 h-4 mr-1" /> Réglages
          </TabsTrigger>
          <TabsTrigger value="wordpress" data-testid="tab-wordpress">
            <Server className="w-4 h-4 mr-1" /> WordPress
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-4">
          <OverviewPanel
            site={site}
            categories={categories}
            tools={tools}
            stacks={stacks}
            onSeed={async () => {
              try {
                const r = await axios.post(
                  `${API}/affiliate/sites/${siteId}/seed-ai-tools`,
                  {}
                );
                toast.success(
                  `Seed OK : ${r.data.inserted?.length || 0} insérés, ${r.data.skipped?.length || 0} ignorés`
                );
                fetchAll();
              } catch {
                toast.error("Échec du seed");
              }
            }}
            onSeedCourse={async () => {
              try {
                const r = await axios.post(
                  `${API}/affiliate/sites/${siteId}/seed-course-tools`,
                  {}
                );
                toast.success(
                  `Cours : ${r.data.inserted?.length || 0} insérés, ${r.data.skipped?.length || 0} ignorés`
                );
                fetchAll();
              } catch {
                toast.error("Échec du seed cours");
              }
            }}
            onSeedEmailing={async () => {
              try {
                const r = await axios.post(
                  `${API}/affiliate/sites/${siteId}/seed-emailing-tools`,
                  {}
                );
                toast.success(
                  `Emailing : ${r.data.inserted?.length || 0} insérés, ${r.data.skipped?.length || 0} ignorés`
                );
                fetchAll();
              } catch {
                toast.error("Échec du seed emailing");
              }
            }}
            onSeedCheckout={async () => {
              try {
                const r = await axios.post(
                  `${API}/affiliate/sites/${siteId}/seed-checkout-tools`,
                  {}
                );
                toast.success(
                  `Caisse : ${r.data.inserted?.length || 0} insérés, ${r.data.skipped?.length || 0} ignorés`
                );
                fetchAll();
              } catch {
                toast.error("Échec du seed caisse");
              }
            }}
            onSeedAccountingFr={async () => {
              try {
                const r = await axios.post(
                  `${API}/affiliate/sites/${siteId}/seed-accounting-fr-tools`,
                  {}
                );
                toast.success(
                  `Compta FR : ${r.data.inserted?.length || 0} insérés, ${r.data.skipped?.length || 0} ignorés`
                );
                fetchAll();
              } catch {
                toast.error("Échec du seed compta FR");
              }
            }}
            onSeedAll={async () => {
              try {
                const r = await axios.post(
                  `${API}/affiliate/sites/${siteId}/seed-all`,
                  {}
                );
                const total =
                  (r.data.ia?.inserted?.length || 0) +
                  (r.data.creation_de_cours?.inserted?.length || 0) +
                  (r.data.emailing?.inserted?.length || 0) +
                  (r.data.caisse_en_ligne?.inserted?.length || 0) +
                  (r.data.comptabilite_facturation_fr?.inserted?.length || 0);
                toast.success(`Seed all : ${total} nouveaux outils insérés`);
                fetchAll();
              } catch {
                toast.error("Échec du seed all");
              }
            }}
          />
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <CategoriesPanel
            siteId={siteId}
            categories={categories}
            onChange={fetchAll}
          />
        </TabsContent>

        <TabsContent value="tools" className="mt-6">
          <ToolsPanel
            siteId={siteId}
            tools={tools}
            categories={categories}
            onChange={fetchAll}
          />
        </TabsContent>

        <TabsContent value="stacks" className="mt-6">
          <StacksPanel
            siteId={siteId}
            stacks={stacks}
            tools={tools}
            onChange={fetchAll}
          />
        </TabsContent>

        <TabsContent value="leadmagnet" className="mt-6">
          <LeadMagnetPanel site={site} onSaved={fetchAll} />
        </TabsContent>

        <TabsContent value="generate" className="mt-6">
          <GeneratePanel
            siteId={siteId}
            site={site}
            categories={categories}
            tools={tools}
            stacks={stacks}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <SettingsPanel site={site} onSaved={fetchAll} />
        </TabsContent>

        <TabsContent value="wordpress" className="mt-6">
          <WordPressPanel site={site} siteId={siteId} onSaved={fetchAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StatBadge = ({ label, value }) => (
  <div className="px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 text-center min-w-[80px]">
    <div className="text-xl font-bold text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 uppercase">{label}</div>
  </div>
);

const OverviewPanel = ({ site, categories, tools, stacks, onSeed, onSeedCourse, onSeedEmailing, onSeedCheckout, onSeedAccountingFr, onSeedAll }) => (
  <div className="grid md:grid-cols-2 gap-4">
    <Card>
      <CardContent className="p-5 space-y-2">
        <h3 className="font-semibold text-slate-900">Identité</h3>
        <Row k="Slug" v={site.slug} />
        <Row k="Domaine" v={site.domain || "—"} />
        <Row k="Langue" v={site.lang} />
        <Row k="Couleur primaire" v={site.primary_color} />
        <Row k="Couleur accent" v={site.accent_color} />
      </CardContent>
    </Card>
    <Card>
      <CardContent className="p-5 space-y-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-1">
          <Wand2 className="w-4 h-4 text-orange-500" /> Packs de seed (idempotents)
        </h3>
        <p className="text-sm text-slate-600">
          Chaque pack injecte des outils curés avec verdicts par persona.
          Les slugs existants sont ignorés.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onSeed} size="sm" variant="outline" data-testid="seed-ai-tools-btn">
            <Wand2 className="w-4 h-4 mr-1" /> IA (14)
          </Button>
          <Button onClick={onSeedCourse} size="sm" variant="outline" data-testid="seed-course-tools-btn">
            <Wand2 className="w-4 h-4 mr-1" /> Cours (10)
          </Button>
          <Button onClick={onSeedEmailing} size="sm" variant="outline" data-testid="seed-emailing-tools-btn">
            <Wand2 className="w-4 h-4 mr-1" /> Emailing (5)
          </Button>
          <Button onClick={onSeedCheckout} size="sm" variant="outline" data-testid="seed-checkout-tools-btn">
            <Wand2 className="w-4 h-4 mr-1" /> Caisse (10)
          </Button>
          <Button onClick={onSeedAccountingFr} size="sm" variant="outline" data-testid="seed-accounting-fr-btn">
            <Wand2 className="w-4 h-4 mr-1" /> Compta 🇫🇷 (4)
          </Button>
          <Button
            onClick={onSeedAll}
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="seed-all-btn"
          >
            <Wand2 className="w-4 h-4 mr-1" /> Seed all
          </Button>
        </div>
        <div className="pt-3 border-t border-slate-100 text-xs text-slate-500 space-y-1">
          <div>Catégories : <span className="font-semibold text-slate-700">{categories.length}</span></div>
          <div>Outils : <span className="font-semibold text-slate-700">{tools.length}</span></div>
          <div>Stacks : <span className="font-semibold text-slate-700">{stacks.length}</span></div>
          <div>Dont 🇫🇷 : <span className="font-semibold text-blue-600">{tools.filter(t => t.country === "France").length}</span></div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between text-sm border-b border-slate-100 py-1.5 last:border-0">
    <span className="text-slate-500">{k}</span>
    <span className="text-slate-900 font-mono text-xs">{v}</span>
  </div>
);

const CategoriesPanel = ({ siteId, categories, onChange }) => {
  const [name, setName] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`${API}/affiliate/categories`, {
        site_id: siteId,
        name: name.trim(),
      });
      toast.success("Catégorie créée");
      setName("");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };
  const del = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" et tous ses outils ?`)) return;
    try {
      await axios.delete(`${API}/affiliate/categories/${id}`);
      toast.success("Catégorie supprimée");
      onChange();
    } catch {
      toast.error("Échec");
    }
  };
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex gap-2 mb-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nouvelle catégorie (ex: CRM, Cybersécurité…)"
            data-testid="new-category-name"
          />
          <Button
            onClick={create}
            data-testid="create-category-btn"
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
        {categories.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">
            Aucune catégorie. La 1ʳᵉ catégorie « IA » est créée automatiquement
            via le seed.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {categories.map((c) => (
              <li
                key={c.id}
                className="py-2 flex items-center justify-between"
                data-testid={`category-row-${c.slug}`}
              >
                <div>
                  <span className="font-medium text-slate-900">{c.name}</span>
                  <span className="text-xs text-slate-500 ml-2 font-mono">
                    /{c.slug}/
                  </span>
                  {c.short_description && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {c.short_description}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => del(c.id, c.name)}
                  className="text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const ToolsPanel = ({ siteId, tools, categories, onChange }) => {
  const [newName, setNewName] = useState("");
  const [newCat, setNewCat] = useState(categories[0]?.id || "");
  const [filterCat, setFilterCat] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!newCat && categories[0]) setNewCat(categories[0].id);
  }, [categories, newCat]);

  const create = async () => {
    if (!newName.trim() || !newCat) {
      toast.error("Nom + catégorie requis");
      return;
    }
    try {
      await axios.post(`${API}/affiliate/tools`, {
        site_id: siteId,
        category_id: newCat,
        name: newName.trim(),
      });
      toast.success("Outil créé");
      setNewName("");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Supprimer l'outil "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/affiliate/tools/${id}`);
      toast.success("Outil supprimé");
      onChange();
    } catch {
      toast.error("Échec");
    }
  };

  const filtered = filterCat
    ? tools.filter((t) => t.category_id === filterCat)
    : tools;

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5 space-y-3">
          <h3 className="font-semibold text-slate-900">Ajouter un outil</h3>
          <div className="grid md:grid-cols-3 gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nom de l'outil"
              data-testid="new-tool-name"
            />
            <select
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              data-testid="new-tool-category"
              className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              <option value="">— Catégorie —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <Button
              onClick={create}
              data-testid="create-tool-btn"
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">
              {filtered.length} outil{filtered.length === 1 ? "" : "s"}
            </h3>
            <select
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              data-testid="tools-filter-category"
              className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              <option value="">Toutes catégories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {filtered.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-6">
              Aucun outil. Utilisez le seed ou ajoutez-en manuellement.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2 font-semibold">Outil</th>
                    <th className="px-3 py-2 font-semibold">Catégorie</th>
                    <th className="px-3 py-2 font-semibold">Prix</th>
                    <th className="px-3 py-2 font-semibold">Note</th>
                    <th className="px-3 py-2 font-semibold">Statut</th>
                    <th className="px-3 py-2 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                      data-testid={`tool-row-${t.slug}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900 flex items-center gap-1.5">
                          {t.name}
                          {t.country === "France" && (
                            <span
                              title="Alternative française"
                              aria-label="Alternative française"
                              data-testid={`fr-flag-${t.slug}`}
                              className="text-base leading-none"
                            >
                              🇫🇷
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 font-mono">
                          {t.slug}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {catName(t.category_id)}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {t.has_free_plan && (
                          <Badge
                            variant="secondary"
                            className="mr-1 bg-emerald-100 text-emerald-700"
                          >
                            Gratuit
                          </Badge>
                        )}
                        {t.price_starting_at
                          ? `${t.price_starting_at} ${t.currency || ""}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {t.rating_overall ? `${t.rating_overall}/5` : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          variant="outline"
                          className={
                            t.status === "active"
                              ? "border-emerald-300 text-emerald-700"
                              : t.status === "archived"
                              ? "border-slate-300 text-slate-500"
                              : "border-amber-300 text-amber-700"
                          }
                        >
                          {t.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(t)}
                          data-testid={`edit-tool-${t.slug}`}
                        >
                          Éditer
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => del(t.id, t.name)}
                          className="text-red-500 hover:bg-red-50 ml-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <ToolEditor
          tool={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChange();
          }}
        />
      )}
    </div>
  );
};

const ToolEditor = ({ tool, categories, onClose, onSaved }) => {
  const [t, setT] = useState({
    ...tool,
    pros: (tool.pros || []).join("\n"),
    cons: (tool.cons || []).join("\n"),
    features: (tool.features || []).join("\n"),
    use_cases: (tool.use_cases || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setT((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...t,
        pros: t.pros.split("\n").map((s) => s.trim()).filter(Boolean),
        cons: t.cons.split("\n").map((s) => s.trim()).filter(Boolean),
        features: t.features.split("\n").map((s) => s.trim()).filter(Boolean),
        use_cases: t.use_cases.split(",").map((s) => s.trim()).filter(Boolean),
        price_starting_at: t.price_starting_at
          ? parseFloat(t.price_starting_at)
          : null,
        rating_overall: t.rating_overall
          ? parseFloat(t.rating_overall)
          : 0,
      };
      // Strip server-managed fields
      delete payload.id;
      delete payload.site_id;
      delete payload.slug;
      delete payload.created_at;
      delete payload.updated_at;

      await axios.put(`${API}/affiliate/tools/${tool.id}`, payload);
      toast.success("Outil mis à jour");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10 pb-10 overflow-auto"
      onClick={onClose}
      data-testid="tool-editor-overlay"
    >
      <Card
        className="w-full max-w-3xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900">
              Éditer : {tool.name}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Fermer
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom">
              <Input
                value={t.name || ""}
                onChange={(e) => upd("name", e.target.value)}
              />
            </Field>
            <Field label="Catégorie">
              <select
                value={t.category_id}
                onChange={(e) => upd("category_id", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Société">
              <Input
                value={t.company || ""}
                onChange={(e) => upd("company", e.target.value)}
              />
            </Field>
            <Field label="Statut">
              <select
                value={t.status || "draft"}
                onChange={(e) => upd("status", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="draft">Brouillon</option>
                <option value="active">Actif</option>
                <option value="archived">Archivé</option>
              </select>
            </Field>
            <Field label="URL site officiel">
              <Input
                value={t.website_url || ""}
                onChange={(e) => upd("website_url", e.target.value)}
              />
            </Field>
            <Field label="URL affilié (trackée)">
              <Input
                value={t.affiliate_url || ""}
                onChange={(e) => upd("affiliate_url", e.target.value)}
              />
            </Field>
            <Field label="Prix de départ">
              <Input
                type="number"
                step="0.01"
                value={t.price_starting_at ?? ""}
                onChange={(e) => upd("price_starting_at", e.target.value)}
              />
            </Field>
            <Field label="Devise">
              <select
                value={t.currency || "USD"}
                onChange={(e) => upd("currency", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </Field>
            <Field label="Note /5">
              <Input
                type="number"
                step="0.1"
                max="5"
                min="0"
                value={t.rating_overall ?? 0}
                onChange={(e) => upd("rating_overall", e.target.value)}
              />
            </Field>
            <Field label="Modèle tarifaire">
              <select
                value={t.pricing_model || "freemium"}
                onChange={(e) => upd("pricing_model", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="free">Gratuit</option>
                <option value="freemium">Freemium</option>
                <option value="trial">Trial</option>
                <option value="paid">Payant</option>
              </select>
            </Field>
          </div>

          <Field label="Description courte (≤120 c.)">
            <Textarea
              rows={2}
              value={t.short_description || ""}
              onChange={(e) => upd("short_description", e.target.value)}
            />
          </Field>

          <Field label="Description longue (markdown)">
            <Textarea
              rows={4}
              value={t.long_description || ""}
              onChange={(e) => upd("long_description", e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Points forts (1 par ligne)">
              <Textarea
                rows={4}
                value={t.pros}
                onChange={(e) => upd("pros", e.target.value)}
              />
            </Field>
            <Field label="Points faibles (1 par ligne)">
              <Textarea
                rows={4}
                value={t.cons}
                onChange={(e) => upd("cons", e.target.value)}
              />
            </Field>
          </div>

          <Field label="Features (1 par ligne)">
            <Textarea
              rows={3}
              value={t.features}
              onChange={(e) => upd("features", e.target.value)}
            />
          </Field>

          <div className="border-t border-slate-100 pt-3">
            <h4 className="font-semibold text-slate-900 mb-2 text-sm">
              Verdicts par persona (différenciant éditorial)
            </h4>
            <div className="space-y-2">
              <Field label="Verdict PME">
                <Textarea
                  rows={2}
                  value={t.verdict_pme || ""}
                  onChange={(e) => upd("verdict_pme", e.target.value)}
                />
              </Field>
              <Field label="Verdict Freelance">
                <Textarea
                  rows={2}
                  value={t.verdict_freelance || ""}
                  onChange={(e) => upd("verdict_freelance", e.target.value)}
                />
              </Field>
              <Field label="Verdict Agence">
                <Textarea
                  rows={2}
                  value={t.verdict_agence || ""}
                  onChange={(e) => upd("verdict_agence", e.target.value)}
                />
              </Field>
            </div>
          </div>

          <Field label="Cas d'usage (séparés par virgule)">
            <Input
              value={t.use_cases}
              onChange={(e) => upd("use_cases", e.target.value)}
              placeholder="redaction, brainstorm, code…"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-tool-btn"
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    {children}
  </div>
);

const StacksPanel = ({ siteId, stacks, tools, onChange }) => {
  const [name, setName] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`${API}/affiliate/stacks`, {
        site_id: siteId,
        name: name.trim(),
      });
      toast.success("Stack créée");
      setName("");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };
  const del = async (id, n) => {
    if (!window.confirm(`Supprimer la stack "${n}" ?`)) return;
    try {
      await axios.delete(`${API}/affiliate/stacks/${id}`);
      toast.success("Stack supprimée");
      onChange();
    } catch {
      toast.error("Échec");
    }
  };
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex gap-2 mb-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nouvelle stack (ex: Stack SEO Agence)"
            data-testid="new-stack-name"
          />
          <Button
            onClick={create}
            data-testid="create-stack-btn"
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Plus className="w-4 h-4 mr-1" /> Créer
          </Button>
        </div>
        {stacks.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">
            Aucune stack. Une « stack » combine 3 outils complémentaires pour un
            cas d'usage (ex : <em>Stack SEO Freelance</em> = Keyword Insights +
            LowFruits + GSC).
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {stacks.map((s) => (
              <li
                key={s.id}
                className="py-3 flex items-start justify-between gap-3"
                data-testid={`stack-row-${s.slug}`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{s.name}</span>
                    <Badge variant="outline" className="font-normal">
                      {s.slug}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={
                        s.status === "active"
                          ? "border-emerald-300 text-emerald-700"
                          : "border-amber-300 text-amber-700"
                      }
                    >
                      {s.status}
                    </Badge>
                  </div>
                  {s.objective && (
                    <p className="text-sm text-slate-600 mt-1">{s.objective}</p>
                  )}
                  <div className="text-xs text-slate-500 mt-1">
                    {(s.tools || []).length} outil(s) · Budget :{" "}
                    {s.total_budget_monthly
                      ? `${s.total_budget_monthly} ${s.currency}/mois`
                      : "—"}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => del(s.id, s.name)}
                  className="text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-slate-400 mt-3">
          L'édition complète (objectif, persona, outils, rationale, budget)
          arrive en Session 2 avec le générateur HTML.
        </p>
      </CardContent>
    </Card>
  );
};

const LeadMagnetPanel = ({ site, onSaved }) => {
  const [f, setF] = useState({
    mailing_provider: site.mailing_provider || "mailerlite",
    mailing_api_key: site.mailing_api_key || "",
    mailing_list_id: site.mailing_list_id || "",
    lead_magnet_title: site.lead_magnet_title || "",
    lead_magnet_url: site.lead_magnet_url || "",
  });
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      await axios.put(`${API}/affiliate/sites/${site.id}`, f);
      toast.success("Lead magnet mis à jour");
      onSaved();
    } catch {
      toast.error("Échec");
    }
  };
  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <h3 className="font-semibold text-slate-900">Lead magnet & emailing</h3>
        <p className="text-xs text-slate-500">
          La capture email est intégrée dans la homepage + footer du site.
          MailerLite est intégré en Session 3 (API call serveur).
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Fournisseur">
            <select
              value={f.mailing_provider}
              onChange={(e) => upd("mailing_provider", e.target.value)}
              data-testid="lm-provider"
              className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              <option value="mailerlite">MailerLite</option>
              <option value="brevo">Brevo</option>
              <option value="mailchimp">Mailchimp</option>
              <option value="none">Aucun</option>
            </select>
          </Field>
          <Field label="ID Liste / Group">
            <Input
              value={f.mailing_list_id}
              onChange={(e) => upd("mailing_list_id", e.target.value)}
              data-testid="lm-list-id"
            />
          </Field>
        </div>
        <Field label="API Key">
          <Input
            type="password"
            value={f.mailing_api_key}
            onChange={(e) => upd("mailing_api_key", e.target.value)}
            data-testid="lm-api-key"
            placeholder="sk_live_…"
          />
        </Field>
        <Field label="Titre du lead magnet">
          <Input
            value={f.lead_magnet_title}
            onChange={(e) => upd("lead_magnet_title", e.target.value)}
            placeholder="Le guide 2026 des 10 meilleurs outils IA"
            data-testid="lm-title"
          />
        </Field>
        <Field label="URL téléchargement (PDF, page perso)">
          <Input
            value={f.lead_magnet_url}
            onChange={(e) => upd("lead_magnet_url", e.target.value)}
            data-testid="lm-url"
          />
        </Field>
        <Button
          onClick={save}
          data-testid="lm-save"
          className="bg-orange-500 hover:bg-orange-600"
        >
          Enregistrer
        </Button>
      </CardContent>
    </Card>
  );
};

const SettingsPanel = ({ site, onSaved }) => {
  const [f, setF] = useState({
    name: site.name,
    domain: site.domain,
    tagline: site.tagline,
    primary_color: site.primary_color,
    accent_color: site.accent_color,
    logo_url: site.logo_url,
    favicon_url: site.favicon_url,
    affiliate_disclosure: site.affiliate_disclosure,
  });
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      await axios.put(`${API}/affiliate/sites/${site.id}`, f);
      toast.success("Réglages enregistrés");
      onSaved();
    } catch {
      toast.error("Échec");
    }
  };
  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <h3 className="font-semibold text-slate-900">Identité & branding</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Nom de la marque">
            <Input
              value={f.name}
              onChange={(e) => upd("name", e.target.value)}
              data-testid="sett-name"
            />
          </Field>
          <Field label="Domaine">
            <Input
              value={f.domain || ""}
              onChange={(e) => upd("domain", e.target.value)}
              data-testid="sett-domain"
            />
          </Field>
        </div>
        <Field label="Tagline">
          <Textarea
            rows={2}
            value={f.tagline}
            onChange={(e) => upd("tagline", e.target.value)}
            data-testid="sett-tagline"
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Couleur primaire">
            <Input
              value={f.primary_color}
              onChange={(e) => upd("primary_color", e.target.value)}
            />
          </Field>
          <Field label="Couleur accent">
            <Input
              value={f.accent_color}
              onChange={(e) => upd("accent_color", e.target.value)}
            />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Logo URL">
            <Input
              value={f.logo_url || ""}
              onChange={(e) => upd("logo_url", e.target.value)}
            />
          </Field>
          <Field label="Favicon URL">
            <Input
              value={f.favicon_url || ""}
              onChange={(e) => upd("favicon_url", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Mention FTC / ARPP (sticky bottom)">
          <Textarea
            rows={2}
            value={f.affiliate_disclosure || ""}
            onChange={(e) => upd("affiliate_disclosure", e.target.value)}
            data-testid="sett-disclosure"
          />
        </Field>
        <Button
          onClick={save}
          data-testid="sett-save"
          className="bg-orange-500 hover:bg-orange-600"
        >
          Enregistrer
        </Button>
      </CardContent>
    </Card>
  );
};

const WordPressPanel = ({ site, siteId, onSaved }) => {
  const [creds, setCreds] = useState({
    wp_site_url: site.wp_site_url || "",
    wp_username: site.wp_username || "",
    wp_app_password: site.wp_app_password || "",
  });
  const [savingCreds, setSavingCreds] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pages, setPages] = useState(null);
  const [loadingPages, setLoadingPages] = useState(false);
  const [locked, setLocked] = useState(new Set(site.wp_locked_pages || []));
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [filterType, setFilterType] = useState("");

  const updCred = (k, v) => setCreds((c) => ({ ...c, [k]: v }));

  const saveCreds = async () => {
    setSavingCreds(true);
    try {
      await axios.put(`${API}/affiliate/sites/${siteId}`, creds);
      toast.success("Identifiants enregistrés");
      onSaved && onSaved();
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSavingCreds(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(
        `${API}/affiliate/sites/${siteId}/wp/test-connection`,
        creds
      );
      setTestResult(r.data);
      if (r.data.success) toast.success(`Connecté en tant que ${r.data.user}`);
      else toast.error(r.data.error || "Échec de la connexion");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur réseau";
      setTestResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const loadPages = async () => {
    setLoadingPages(true);
    try {
      const r = await axios.get(
        `${API}/affiliate/sites/${siteId}/wp/publishable-pages`
      );
      setPages(r.data);
      setLocked(new Set(r.data.pages.filter((p) => p.locked).map((p) => p.slug)));
    } catch {
      toast.error("Échec du chargement des pages");
    } finally {
      setLoadingPages(false);
    }
  };

  useEffect(() => {
    loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const toggleLock = (slug) => {
    const n = new Set(locked);
    if (n.has(slug)) n.delete(slug);
    else n.add(slug);
    setLocked(n);
  };

  const saveLocks = async () => {
    try {
      await axios.put(`${API}/affiliate/sites/${siteId}/wp/lock`, {
        locked_slugs: Array.from(locked),
      });
      toast.success(`${locked.size} page${locked.size > 1 ? "s" : ""} verrouillée${locked.size > 1 ? "s" : ""}`);
      loadPages();
      onSaved && onSaved();
    } catch {
      toast.error("Échec");
    }
  };

  const publish = async () => {
    if (!creds.wp_site_url) {
      toast.error("Configurez d'abord vos identifiants WordPress");
      return;
    }
    if (!window.confirm(`Publier ${pages?.publishable || "?"} pages sur ${creds.wp_site_url} ? Les pages verrouillées seront ignorées.`))
      return;
    setPublishing(true);
    setPublishResult(null);
    try {
      const r = await axios.post(
        `${API}/affiliate/sites/${siteId}/wp/publish`,
        {},
        { timeout: 600000 } // 10 min for large sites
      );
      setPublishResult(r.data);
      if (r.data.errors === 0) {
        toast.success(`${r.data.published} pages publiées avec succès`);
      } else {
        toast.warning(`${r.data.published} publiées · ${r.data.errors} erreurs`);
      }
      onSaved && onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur de publication";
      toast.error(msg);
    } finally {
      setPublishing(false);
    }
  };

  const filteredPages = pages?.pages?.filter(
    (p) => !filterType || p.page_type === filterType
  ) || [];
  const pageTypes = pages?.pages
    ? Array.from(new Set(pages.pages.map((p) => p.page_type)))
    : [];

  return (
    <div className="space-y-4" data-testid="wordpress-panel">
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Server className="w-5 h-5 text-orange-500" /> Identifiants WordPress
          </h3>
          <p className="text-sm text-slate-600">
            Créez un{" "}
            <a
              href="https://wordpress.org/documentation/article/application-passwords/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 hover:underline"
            >
              mot de passe d'application
            </a>{" "}
            dans WP Admin → Utilisateurs → Profil. Le rôle doit pouvoir éditer
            les pages (Éditeur ou Admin).
          </p>
          <Field label="URL du site WordPress">
            <Input
              data-testid="wp-site-url"
              value={creds.wp_site_url}
              onChange={(e) => updCred("wp_site_url", e.target.value)}
              placeholder="https://lastackprime.fr"
            />
          </Field>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nom d'utilisateur">
              <Input
                data-testid="wp-username"
                value={creds.wp_username}
                onChange={(e) => updCred("wp_username", e.target.value)}
                placeholder="admin"
              />
            </Field>
            <Field label="Mot de passe d'application">
              <Input
                data-testid="wp-app-password"
                type="password"
                value={creds.wp_app_password}
                onChange={(e) => updCred("wp_app_password", e.target.value)}
                placeholder="xxxx xxxx xxxx xxxx"
              />
            </Field>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={saveCreds}
              disabled={savingCreds}
              variant="outline"
              data-testid="wp-save-creds"
            >
              {savingCreds ? "Enregistrement…" : "Enregistrer"}
            </Button>
            <Button
              onClick={testConnection}
              disabled={testing}
              data-testid="wp-test-conn"
            >
              {testing ? "Test…" : "Tester la connexion"}
            </Button>
          </div>
          {testResult && (
            <div
              className={`p-3 rounded-md text-sm ${
                testResult.success
                  ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                  : "bg-red-50 border border-red-200 text-red-700"
              }`}
            >
              {testResult.success
                ? `✅ Connecté en tant que ${testResult.user} (rôles : ${(testResult.roles || []).join(", ")})`
                : `❌ ${testResult.error}`}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-slate-900">Pages à publier</h3>
              {pages && (
                <p className="text-sm text-slate-600">
                  {pages.publishable} pages publiables ·{" "}
                  <span className="text-amber-700">
                    {locked.size} verrouillée{locked.size > 1 ? "s" : ""}
                  </span>{" "}
                  · Total : {pages.total}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
                data-testid="wp-pages-type-filter"
              >
                <option value="">Tous les types</option>
                {pageTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={saveLocks}
                data-testid="wp-save-locks"
              >
                Enregistrer les verrous
              </Button>
            </div>
          </div>

          {loadingPages ? (
            <p className="text-slate-500 text-sm py-4 text-center">
              Chargement…
            </p>
          ) : (
            <div className="max-h-96 overflow-y-auto border border-slate-100 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2 font-semibold w-10">🔒</th>
                    <th className="px-3 py-2 font-semibold">Slug</th>
                    <th className="px-3 py-2 font-semibold">Titre</th>
                    <th className="px-3 py-2 font-semibold">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.map((p) => (
                    <tr
                      key={`${p.parent_path || "_root_"}/${p.slug}`}
                      className={`border-b border-slate-100 last:border-0 ${
                        locked.has(p.slug) ? "bg-amber-50/40" : ""
                      }`}
                      data-testid={`wp-row-${p.slug}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={locked.has(p.slug)}
                          onChange={() => toggleLock(p.slug)}
                          className="accent-amber-500 cursor-pointer"
                          data-testid={`wp-lock-${p.slug}`}
                          aria-label={`Verrouiller ${p.slug}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">
                        {p.parent_path ? `${p.parent_path}/` : ""}
                        {p.slug}
                      </td>
                      <td className="px-3 py-2 text-slate-900">{p.title}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs">
                          {p.page_type}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-slate-900">Publication</h3>
              <p className="text-sm text-slate-600">
                {site.wp_published ? (
                  <>
                    Dernière publication :{" "}
                    {site.wp_last_publish_at
                      ? new Date(site.wp_last_publish_at).toLocaleString()
                      : "—"}{" "}
                    · {site.wp_last_publish_summary?.published || 0} pages OK
                  </>
                ) : (
                  "Jamais publié."
                )}
              </p>
            </div>
            <Button
              onClick={publish}
              disabled={publishing || !creds.wp_site_url}
              data-testid="wp-publish-btn"
              className="bg-orange-500 hover:bg-orange-600"
            >
              {publishing ? "Publication en cours…" : "Publier maintenant"}
            </Button>
          </div>
          {publishResult && (
            <div className="text-sm space-y-1 pt-3 border-t border-slate-100">
              <div className="flex gap-4 flex-wrap">
                <span>
                  Total : <strong>{publishResult.total}</strong>
                </span>
                <span className="text-emerald-700">
                  Publiées : <strong>{publishResult.published}</strong>
                </span>
                <span className="text-amber-700">
                  Verrouillées :{" "}
                  <strong>{publishResult.skipped_locked?.length || 0}</strong>
                </span>
                <span className="text-red-600">
                  Erreurs : <strong>{publishResult.errors}</strong>
                </span>
              </div>
              {publishResult.error_details?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-red-600 text-xs">
                    Voir les erreurs ({publishResult.error_details.length})
                  </summary>
                  <ul className="mt-2 text-xs text-slate-600 max-h-40 overflow-auto space-y-1">
                    {publishResult.error_details.slice(0, 20).map((e, i) => (
                      <li key={i} className="font-mono">
                        <strong>{e.slug}</strong> : {e.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const GeneratePanel = ({ siteId, site, categories, tools, stacks }) => {
  const [index, setIndex] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/affiliate/sites/${siteId}/page-index`)
      .then((r) => setIndex(r.data))
      .catch(() => null);
  }, [siteId, tools.length, categories.length, stacks.length]);

  const previewUrl = (path) => `${API}/affiliate/sites/${siteId}/preview${path}`;

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/affiliate/sites/${siteId}/generate-zip`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${site.slug}-static-site.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ZIP téléchargé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du téléchargement");
    } finally {
      setDownloading(false);
    }
  };

  const categoriesSorted = [...categories].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const toolsByCat = tools.reduce((acc, t) => {
    (acc[t.category_id] = acc[t.category_id] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="generate-panel">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <FileCode className="w-5 h-5 text-orange-500" />
                Générateur HTML statique
              </h3>
              <p className="text-sm text-slate-600">
                Aperçu navigateur de chaque page · Téléchargement du site complet
                en ZIP (prêt pour WordPress, Cloudflare Pages ou Bunny CDN).
              </p>
            </div>
            <Button
              onClick={downloadZip}
              disabled={downloading || tools.length === 0}
              data-testid="download-zip-btn"
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? "Préparation…" : "Télécharger le ZIP"}
            </Button>
          </div>

          {index && (
            <div className="grid grid-cols-2 md:grid-cols-7 gap-3 pt-3 border-t border-slate-100">
              <Stat n={index.homepage} label="Homepage" />
              <Stat n={index.categories} label="Catégories" />
              <Stat n={index.tool_reviews} label="Fiches outils" />
              <Stat n={index.alternatives} label="Alternatives" />
              <Stat n={index.comparisons} label="Comparatifs" />
              <Stat n={index.best_for_profession || 0} label="Top par profil" />
              <Stat n={index.total_pages} label="Total pages" highlight />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 mb-3">
            Aperçus rapides
          </h3>
          <div className="space-y-3">
            <PreviewLink
              href={previewUrl("")}
              title="Homepage"
              subtitle="/"
              testid="preview-homepage"
            />
            <PreviewLink
              href={previewUrl("/about")}
              title="À propos (méthodologie)"
              subtitle="/a-propos/"
              testid="preview-about"
            />
            <PreviewLink
              href={previewUrl("/contact")}
              title="Contact"
              subtitle="/contact/"
              testid="preview-contact"
            />
            <PreviewLink
              href={previewUrl("/legal")}
              title="Mentions légales"
              subtitle="/mentions-legales/"
              testid="preview-legal"
            />
            {categoriesSorted.map((c) => {
              const catTools = toolsByCat[c.id] || [];
              const firstTool = catTools[0];
              return (
                <div
                  key={c.id}
                  className="pl-3 border-l-2 border-slate-100 space-y-2"
                >
                  <PreviewLink
                    href={previewUrl(`/category/${c.slug}`)}
                    title={`Catégorie : ${c.name}`}
                    subtitle={`/${c.slug}/`}
                    testid={`preview-cat-${c.slug}`}
                  />
                  <PreviewLink
                    href={previewUrl(`/best-for/${c.slug}/pme`)}
                    title={`Top ${c.name} pour PME`}
                    subtitle={`/${c.slug}/meilleur-pour-pme/`}
                    testid={`preview-best-for-pme-${c.slug}`}
                    nested
                  />
                  {firstTool && (
                    <>
                      <PreviewLink
                        href={previewUrl(`/tool/${c.slug}/${firstTool.slug}`)}
                        title={`Fiche outil : ${firstTool.name}`}
                        subtitle={`/${c.slug}/${firstTool.slug}-avis/`}
                        testid={`preview-tool-${firstTool.slug}`}
                        nested
                      />
                      <PreviewLink
                        href={previewUrl(
                          `/alternatives/${firstTool.slug}`
                        )}
                        title={`Alternatives : ${firstTool.name}`}
                        subtitle={`/alternatives/alternative-a-${firstTool.slug}/`}
                        testid={`preview-alts-${firstTool.slug}`}
                        nested
                      />
                      {catTools[1] && (
                        <PreviewLink
                          href={previewUrl(
                            `/comparison/${firstTool.slug}/${catTools[1].slug}`
                          )}
                          title={`Comparatif : ${firstTool.name} vs ${catTools[1].name}`}
                          subtitle={`/comparatifs/${firstTool.slug}-vs-${catTools[1].slug}/`}
                          testid={`preview-vs-${firstTool.slug}`}
                          nested
                        />
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-slate-500 italic">
        ℹ️ Le tracker de clics affiliés (<code className="text-orange-600">/api/affiliate/go/&#123;site&#125;/&#123;outil&#125;</code>),
        la capture email (lead magnet + Mon score express) et les pages
        About/Contact/Legal sont actifs. La publication WordPress directe et
        l'éditeur de Stacks complet arrivent en Session 3b.
      </p>
    </div>
  );
};

const Stat = ({ n, label, highlight = false }) => (
  <div
    className={`text-center p-3 rounded-lg ${
      highlight
        ? "bg-orange-50 border border-orange-200"
        : "bg-slate-50 border border-slate-200"
    }`}
  >
    <div
      className={`text-2xl font-bold ${
        highlight ? "text-orange-600" : "text-slate-900"
      }`}
    >
      {n}
    </div>
    <div className="text-[11px] text-slate-500 uppercase">{label}</div>
  </div>
);

const PreviewLink = ({ href, title, subtitle, testid, nested = false }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors ${
      nested ? "ml-2" : ""
    }`}
  >
    <div>
      <div className="text-sm font-medium text-slate-900">{title}</div>
      <div className="text-xs text-slate-500 font-mono">{subtitle}</div>
    </div>
    <ExternalLink className="w-4 h-4 text-slate-400" />
  </a>
);

